import React, { useContext, useMemo, useState } from 'react';
import { t } from '@lingui/macro';
import { connect } from 'react-redux';
import dayjs from 'dayjs';
import isBetween from 'dayjs/plugin/isBetween';
import { List, Spin, Row } from 'antd';
import InfiniteScroll from 'react-infinite-scroller';
import { FileContext } from '../Provider';
import FileCard from './FileCard';
import CustomPagination from '../../CustomPagination';
import queryString from "query-string";
import { history } from "bukku/redux/store";

dayjs.extend(isBetween);

export const FileList = connect(
    state => ({
        isPulling: state.App.isPulling,
        view: state.App.view,
        pagination: state.File.pagination,
        globalFiles: state.File.globalFiles,
    }), {
    })(props => {
    const {
        // redux
        isPulling,
        view,
        pagination,
    } = props;
    const {
        // parent
        rowIdList,
        newlyUploadedIds,
        onFileCardClick,

        fileList, // either for infinite scroll list (FileSelect) or for pagination (FileManager)
        currentPage: providerCurrentPage,

        // for infinite scroll list (FileSelect)
        hasMore,
        loading,
        handleInfiniteOnLoad,

        // for pagination (FileManager)
        handlePaginationChange,

        type, // 'fileSelect' or 'fileManager'
    } = props;

    const {location} = window;
    const {page, page_size} = queryString.parse(location.search);

    const [current, setCurrent] = useState(parseInt(page, 10) || 1);
    const [pageSize, setPageSize] = useState(parseInt(page_size, 10) || 30);

    const fileContext = useContext(FileContext);
    const { contextProps } = fileContext;
    const { mode } = contextProps;

    useMemo(() => {
        // if user searched for a value / clear search, set the pagination back to page 1.
        // providerCurrentPage will always be 1 in this case
        if (providerCurrentPage !== current)
            setCurrent(providerCurrentPage);
    }, [providerCurrentPage, current]);

    const groupFilesByTime = useMemo(
        () => {
            let data = fileList;
            if (type === 'fileManager') {
                // when files are uploaded, fileList will contain > pageSize files, so we slice the list.
                data = fileList.length > pageSize ?
                    fileList.slice(0, pageSize) : fileList;
            }

            const timeLabel = {
                TODAY: t`Uploaded Today`,
                YESTERDAY: t`Uploaded Yesterday`,
                '7DAY': t`Uploaded within 7 days`,
                '30DAY': t`Uploaded within 30 days`,
                LONGTIMEAGO: t`Uploaded more than 30 days ago`,
            };

            const todayFiles = data.filter(f => dayjs().isSame(f.created_at, 'day'));
            const yesterdayFiles = data
                .filter(f => dayjs().subtract(1, 'day').isSame(f.created_at, 'day'));

            // if yesterday NO files, 7DAY: calculate exactly from 7 days ago -> today (daysToExclude == 0)
            // if yesterday HAVE files, 7DAY: calculate from 7 days ago to 1 day ago.  (daysToExclude == 1)
            const daysToExclude = !!yesterdayFiles.length; // used for 7day,30day,longtimeago

            const momentFilterObject = {
                TODAY: todayFiles,
                YESTERDAY: yesterdayFiles,
                '7DAY': data.filter(f => dayjs(f.created_at)
                    .isBetween(dayjs().subtract(7, 'day').startOf('date'),
                        dayjs().subtract(daysToExclude, 'day').startOf('date'))),
                '30DAY': data.filter(f => dayjs(f.created_at)
                    .isBetween(dayjs().subtract(30, 'day').startOf('date'),
                        dayjs().subtract(8, 'day').endOf('date'))),
                LONGTIMEAGO: data.filter(f => dayjs(f.created_at)
                    .isBefore(dayjs().subtract(31, 'day').endOf('date'))),
            };

            const timeFileList = [];
            for (const [key, value] of Object.entries(momentFilterObject)) {
                if (value.length) {
                    timeFileList.push(
                        {
                            key,
                            label: timeLabel[key],
                            data: value,
                        },
                    );
                }
            }

            console.log('aaa function groupFilesByTime is running');
            return timeFileList;
        },
        [fileList, pageSize, type],
    );

    // for FileManager
    const paginationData = useMemo(() => ({
        total: pagination.total,
        current,
        pageSize,
        groupFilesByTime,
    }), [current, pageSize, groupFilesByTime, pagination.total]);

    // for FileManager
    const onPageChange = (nextPage, pageSize) => {
        setCurrent(nextPage);
        setPageSize(pageSize);

        let searchString = '';
        const newParams = {};

        // if page = 1 and pageSize = 30, don't put in the URL to make it look cleaner
        if (nextPage !== 1)
            newParams.page = nextPage;
        if (pageSize !== 30)
            newParams.page_size = pageSize;

        const paramString = queryString.stringify(newParams, { arrayFormat: 'bracket', skipNull: true });

        if (paramString)
            searchString = `?${paramString}`;

        if (location.search !== searchString)
            history.replace(location.pathname + searchString);

        handlePaginationChange(nextPage, pageSize);
    };

    // for FileManager
    const renderPagination = () => (
        <div className="pagination-div">
            <CustomPagination
                paginationData={paginationData}
                onPageChange={onPageChange}
            />
        </div>
    );

    // render data as file cards given by dataSource.
    const renderFileListItems = dataSource => <List
        grid={{
            gutter: view === 'DesktopView' ? [16, 16] : [8, 8],
            column: 4,
            xs: 1,
            sm: 2,
            md: 3,
            lg: 4,
            xl: 4,
            xxl: 5,
        }}
        dataSource={dataSource}
        renderItem={item => (
            <List.Item
                className="file-list-item"
                onClick={
                    //  eslint-disable-next-line no-underscore-dangle
                    !item._isSkeleton ? () => onFileCardClick(item, mode) : undefined
                }
            >
                <FileCard
                    selectedFileIds={rowIdList}
                    fileItem={item}
                    newlyUploadedIds={newlyUploadedIds}
                    type={type}
                />
            </List.Item>
        )}
    />;

    const renderGroups = () => groupFilesByTime.map(item => (
        <div className="time-group" key={item.key}>
            <div className="label">
                {item.label}
            </div>
            <div className="items">
                {renderFileListItems(item.data, item.key)}
            </div>
        </div>
    ));

    const renderFileManagerList = () => (
        <>
            {renderPagination()}
            <Spin spinning={isPulling}>
                {renderGroups()}
            </Spin>
            <div style={{paddingTop: "8px"}}>
                {renderPagination()}
            </div>

        </>
    );

    const renderFileSelectList = () => (
        <InfiniteScroll
            className="infinite-scroll"
            initialLoad={false}
            loadMore={handleInfiniteOnLoad}
            hasMore={!loading && hasMore}
            useWindow={false}
            threshold={10}
            loader={(loading) ?
                <Row key="loader" style={{ flexDirection: 'column', alignContent: 'center' }}>
                    <Spin />
                </Row> :
                null}
        >
            <Spin spinning={isPulling}>
                {renderGroups()}
            </Spin>
        </InfiniteScroll>
    );

    return (
        <div
            id="file-list-icon-list"
            className="file-list-icon-list "
            style={{ overflow: type === 'fileManager' ? 'hidden' : null }}
        >
            {type === 'fileSelect' ?
                renderFileSelectList() :
                renderFileManagerList()}
        </div>
    );
});
